import { Can } from '@casl/react'
import { Help } from '@mui/icons-material'
import { ListSubheader, MenuItem } from '@mui/material'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import FormControl from '@mui/material/FormControl'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'
import { styled } from '@mui/system'
import { TFunction } from 'i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import spacetime from 'spacetime'
import useEventReassignment from '../../hooks/useEventReassignment'
import { useFetchEventOptions } from '../../hooks/events/useFetchEventOptions'
import { useMountEffect } from '../../hooks/useMountEffect'
import {
  FetchEventsResponse,
  OrderByDirection,
  PaginationResponse,
} from '../../swagger/models'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../Buttons/OutlinedButton'
import CardFormHeader from '../Card/CardFormHeader'
import EventCardMobile from '../Card/EventCardMobile'
import TodaysEventCard from '../Card/TodaysEventCard'
import DateField, { DateFieldVariant } from '../Elements/DateField'
import Header, { HeaderVariant } from '../Elements/Header'
import { Page } from '../Elements/PageMargins'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import ReassignEventOwnerModal from '../Modals/ReassignEventOwnerModal'
import { useAuth } from '../Routes/AuthProvider'
import SearchBar from '../Search/SearchBar'
import { EventSortBy, EventSortOptions } from './EventEnums'
import { createSpacetime, EventFilter, getTodaysEarliestEvent } from './Events'
import EventSummaryTable from './EventSummaryTable'

const StyledHeader = styled(Header)(({ theme }) => ({
  margin: theme.spacing(3, 'auto'),
  padding: 0,
}))

interface EventsTabProps {
  events: FetchEventsResponse[]
  isLoading: boolean
  minDateFilter: string
  dateToFilter: string
  handleEventFilterChange: <K extends keyof EventFilter>(
    filter: K,
    value: EventFilter[K]
  ) => void
  handlePaginationChange: (pagination: PaginationResponse) => void
  pagination: PaginationResponse
}

const labelForSortOption = (
  sortOption: EventSortOptions,
  t: TFunction
): string => {
  switch (sortOption) {
    case EventSortOptions.EventName:
      return t('Team.SortOptions.EventName', 'Sort By: Event Name')
    case EventSortOptions.StartTime:
      return t('Team.SortOptions.StartTime', 'Sort By: Start Time')
    case EventSortOptions.CreatedBy:
      return t('Team.SortOptions.CreatedBy', 'Sort By: Created By')
    default:
      return t('Team.SortOptions.StartDate', 'Sort By: Start Date')
  }
}

export const EventsTab: React.FunctionComponent<EventsTabProps> = (props) => {
  const {
    events,
    isLoading,
    minDateFilter,
    dateToFilter,
    handleEventFilterChange,
    handlePaginationChange,
    pagination,
  } = props

  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const { permissionAbility } = useAuth()
  const eventsReassignment = useEventReassignment({
    onReassignComplete: handlePaginationChange,
    currentPage: pagination.page,
    pageSize: pagination.pageSize,
    totalCount: pagination.totalCount,
    events,
  })
  const isMobileOrSmaller = useMediaQuery(theme.breakpoints.down('sm'))

  const [eventTypeValue, setEventTypeValue] = useState('')
  const [filterType, setFilterType] = useState('')
  const [paramValue, setParamValue] = useState('')
  const [eventTypeMenuOpen, setEventTypeMenuOpen] = useState(false)

  const todayEvent = useMemo(() => getTodaysEarliestEvent(events), [events])

  const isNextEventToday = todayEvent
    ? spacetime.now().isSame(createSpacetime(todayEvent), 'day')
    : true

  const translationText = t(
    'Events.AddTittleToolTip.TittleTranslation',
    'Search for events by date, past or future, to see all events on that date and after'
  )

  const allEventTypesOption = {
    id: '__ALL__',
    name: t('Events.FilterOption.AllEventTypes', 'All Event Types'),
  }

  const { triggerFetch, eventTypes } = useFetchEventOptions({
    includeRoles: false,
  })

  useMountEffect(triggerFetch)

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleEventFilterChange('fromDate', event.target.value)
  }

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault()
  }

  const handleDropDownSelection = (selection: string, id: string) => {
    switch (id) {
      case 'filterByEventCreator':
        setParamValue(selection)
        setFilterType('eventCreator')
        break
      case 'sort':
        let orderByProperty: string = EventSortBy.StartDate
        switch (selection) {
          case EventSortOptions.StartDate:
            orderByProperty = EventSortBy.StartDate
            break
          case EventSortOptions.StartTime:
            orderByProperty = EventSortBy.StartTime
            break
          case EventSortOptions.CreatedBy:
            orderByProperty = EventSortBy.CreatedBy
            break
          case EventSortOptions.EventName:
            orderByProperty = EventSortBy.EventName
            break
          default:
            break
        }

        let orderDirection = OrderByDirection.Asc
        if (Object.keys(pagination.orderBy[0]).includes(orderByProperty)) {
          orderDirection = Object.values(pagination.orderBy[0]).includes(
            OrderByDirection.Asc
          )
            ? OrderByDirection.Desc
            : OrderByDirection.Asc
        }

        handlePaginationChange({
          ...pagination,
          page: 0,
          orderBy: [{ [orderByProperty]: orderDirection }],
        })
        break
    }
  }

  const handleChangeEventType = (event: SelectChangeEvent<string>) => {
    const normalized = event.target.value.trim()
    const newEventType = normalized === allEventTypesOption.id ? '' : normalized

    setEventTypeValue(newEventType)
    handleEventFilterChange('eventType', newEventType)
  }

  const handleSearch = (text: string) => {
    handleEventFilterChange('search', text)
  }

  useEffect(() => {
    if (filterType) {
      handleEventFilterChange(filterType as keyof EventFilter, paramValue)
    }
  }, [filterType, paramValue, handleEventFilterChange])

  const dateOnOrAfterFilter = () => (
    <Box display="flex" alignItems="center">
      <DateField
        dateVariant={DateFieldVariant.EventsByDate}
        inputProps={{
          min: `${minDateFilter}`,
        }}
        variant="filled"
        type="date"
        fullWidth
        onChangeForParent={handleDateChange}
        value={dateToFilter}
        onKeyDown={handleOnKeyDown}
        hideLabel
        sx={{
          '& input': {
            height: '100%',
            padding: theme.spacing(1.3),
          },
        }}
        InputProps={{
          disableUnderline: true,
          sx: { height: theme.spacing(4.5) },
        }}
      />
      <Tooltip title={translationText}>
        <Help />
      </Tooltip>
    </Box>
  )

  const navigateToEventDetailsPage = (id: number, eventName: string) => {
    navigate(
      { pathname: `/events/event-details/${id}` },
      { state: { eventName } }
    )
  }

  const eventTypeMenuItems = useMemo(() => {
    return Object.entries(eventTypes).flatMap(([category, items]) => {
      const header = (
        <ListSubheader disableSticky key={`hdr-${category}`}>
          {category}
        </ListSubheader>
      )
      const options = items.map(({ id, name }) => (
        <MenuItem key={id} value={id} sx={{ pl: 4 }}>
          {name}
        </MenuItem>
      ))
      return [header, ...options]
    })
  }, [eventTypes])

  const EventTypeSelect = (
    <FormControl
      variant="outlined"
      sx={{ minWidth: 240 }}
      fullWidth={isMobileOrSmaller}
    >
      <Select
        id="filterByEventType"
        displayEmpty
        value={eventTypeValue || allEventTypesOption.id}
        onChange={handleChangeEventType}
        open={eventTypeMenuOpen}
        onOpen={() => setEventTypeMenuOpen(true)}
        onClose={() => setEventTypeMenuOpen(false)}
        renderValue={(selected) => {
          const current = String(selected || '').trim()
          const label =
            current === allEventTypesOption.id
              ? allEventTypesOption.name
              : current
          return (
            <Box
              component="span"
              sx={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.8,
              }}
            >
              {label}
            </Box>
          )
        }}
        sx={{
          borderRadius: 1.5,
          '& .MuiSelect-select': {
            py: 0.75,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'text.secondary',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'text.primary',
          },
          '& .MuiSelect-icon': { right: 10 },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 1,
              py: 0.5,
              backgroundColor: '#FEFAF7',
              '& .MuiMenuItem-root': { fontSize: '0.95rem', color: '#1f2937' },
              '& .MuiListSubheader-root': {
                fontWeight: 600,
                fontSize: '0.94rem',
                lineHeight: 2,
                color: '#000',
                textTransform: 'uppercase',
              },
            },
          },
        }}
      >
        <MenuItem value={allEventTypesOption.id}>
          {allEventTypesOption.name}
        </MenuItem>

        {eventTypeMenuItems}
      </Select>
    </FormControl>
  )

  return (
    <Page>
      {isMobileOrSmaller ? (
        <>
          <StyledHeader
            id="todayEventHeader"
            headerName={
              isNextEventToday
                ? t('Events.Card.Header.Today', "Today's Event")
                : t('Events.Card.Header.Upcoming', 'Upcoming Event')
            }
          />

          {todayEvent && (
            <EventCardMobile
              event={todayEvent}
              todaysEvent
              isLoading={isLoading}
            />
          )}
          <Box
            display="flex"
            justifyContent="center"
            margin={theme.spacing(0, 'auto')}
          >
            <SearchBar
              handleSearch={handleSearch}
              clearAndHandleSearch={() => handleSearch('')}
            />
          </Box>
          <section>
            <Box display="flex" justifyContent={'space-between'} sx={{ px: 2 }}>
              <Box sx={{ flex: 1, mr: 2 }}>{EventTypeSelect}</Box>
            </Box>
            <Box textAlign="center">{dateOnOrAfterFilter()}</Box>
          </section>
          <StyledHeader
            id="eventsHeader"
            headerName={t('Events.Card.Header.Events', 'Events')}
          />

          <Box textAlign={'center'}>
            <DropDown
              fullWidth
              cssProps={{
                display: 'flex',
                justifyContent: 'center',
                maxWidth: `calc(100vw - ${theme.spacing(4)})`,
              }}
              id="sort"
              menuOptions={Object.values(EventSortOptions).map((option) => ({
                name: labelForSortOption(option, t),
                id: option,
              }))}
              defaultValue={t('Events.SortOption.Sort', 'Sort')}
              handleSelection={handleDropDownSelection}
              variant={DropDownVariant.SortAndFilter}
            />
          </Box>

          {events.map((eventItem) =>
            eventItem ? (
              <EventCardMobile
                key={eventItem.eventKey}
                event={eventItem}
                isLoading={isLoading}
              />
            ) : null
          )}
          {pagination.totalCount > pagination.pageSize && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: theme.spacing(6, 0),
              }}
            >
              <OutlinedButton
                data-testid="viewMoreButton"
                id="viewMoreDraftEvents"
                onClick={() =>
                  handlePaginationChange({
                    ...pagination,
                    pageSize: pagination.pageSize + 10,
                    page: 0,
                  })
                }
                variant={OutlinedButtonVariant.ViewMore}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          {todayEvent && (
            <TodaysEventCard
              isLoading={isLoading}
              onClick={() =>
                navigateToEventDetailsPage(
                  (todayEvent.eventKey as number) ?? 0,
                  todayEvent.name
                )
              }
              todaysEvent={todayEvent}
              headerLabel={
                isNextEventToday
                  ? t('Events.Card.Header.Today', "Today's Event")
                  : t('Events.Card.Header.Upcoming', 'Upcoming Event')
              }
            />
          )}
          {eventsReassignment && (
            <ReassignEventOwnerModal
              open={eventsReassignment.isReassignModalOpen}
              onClose={eventsReassignment.handleCloseReassignModal}
              onReassign={eventsReassignment.handleReassignOwners}
              selectedCount={eventsReassignment.selectedRows.length}
              selectedEventOwner={eventsReassignment.selectedOwner}
              setSelectedEventOwner={eventsReassignment.setSelectedOwner}
              eventOwnerOptions={eventsReassignment.ownerOptions}
              isLoading={eventsReassignment.isLoadingOwners}
              isReassigning={eventsReassignment.isReassigning}
              onInputChange={eventsReassignment.setOwnerSearchInput}
            />
          )}
          <Card>
            <CardFormHeader
              header={
                <Header
                  id="events-header"
                  headerName={t('Events.Card.Header', 'Events')}
                  component="h2"
                  variant={HeaderVariant.Card}
                />
              }
              buttons={
                <Box display="flex" alignItems="center">
                  <Can I="bulkReassign" on="Event" ability={permissionAbility}>
                    <Box margin={theme.spacing(0, 3)}>
                      <OutlinedButton
                        id="reassignEventOwners"
                        variant={OutlinedButtonVariant.ReassignEvents}
                        onClick={eventsReassignment.handleOpenReassignModal}
                        disabled={eventsReassignment.selectedRows.length === 0}
                      />
                    </Box>
                  </Can>
                  <Box>
                    <SearchBar
                      handleSearch={handleSearch}
                      sx={{ '& .MuiOutlinedInput-root': { margin: 0 } }}
                    />
                  </Box>

                  <Box margin={theme.spacing(0, 3)} sx={{ minWidth: 240 }}>
                    {EventTypeSelect}
                  </Box>

                  <Tooltip
                    data-testid="tooltip-events-by-date"
                    title={t('Tooltip.Title.EventsByDate', 'Events By Date')}
                  >
                    {dateOnOrAfterFilter()}
                  </Tooltip>
                </Box>
              }
            />
            <Box>
              <EventSummaryTable
                events={events}
                isLoading={isLoading}
                handlePaginationChange={handlePaginationChange}
                pagination={pagination}
                checkboxSelection={permissionAbility.can(
                  'bulkReassign',
                  'Event'
                )}
                onSelectionModelChange={
                  eventsReassignment.handleSelectionChange
                }
                selectionModel={eventsReassignment.selectedRows}
              />
            </Box>
          </Card>
        </>
      )}
    </Page>
  )
}

export default EventsTab
